import { useSelector } from 'react-redux';
import {
  Navigate,
  Outlet,
  useLocation,
  useOutletContext,
} from 'react-router-dom';
import { selectToken } from '../store/auth/selectors';

import ProtectedResource, {
  ProtectedResourceProps,
} from 'components/RoleBasedAccessControl/ProtectedResource';
import { usePermissions } from 'context/PermissionsContext';
import useCountryVerify from 'hooks/useCountryVerify';
import useRouteConfig from 'hooks/useRouteConfig';

interface ProtectedRouteProps {
  authorizedPermission?: ProtectedResourceProps['permission'];
}

/**
 *
 * This component is used to prevent unauthenticated and unauthorized access to protected routes.
 * - If `authorizedPermission` is provided, it renders the protected route only if the user has specified roles.
 * - If `authorizedPermission` is not provided, it renders the protected route for the roles which has access to the application.
 *
 * @component
 * @example
 * // Example usage of ProtectedRoute within a Route
 * <Route path="/admin" element={<ProtectedRoute authorizedPermission={['admin']}/>}>
 *    <Route path="/admin" element={<Admin />} />
 * </Route>
 *
 * @param {PermissionKey | Array<PermissionKey>} [props.authorizedPermission] - An optional array or string
 * of PermissionKey that are required to access the protected route.
 *
 * @returns {JSX.Element} - Returns an Outlet component if the user is authenticated and
 * authorized; otherwise, it redirects to the login page.
 */
const ProtectedRoute = ({ authorizedPermission }: ProtectedRouteProps) => {
  const token = useSelector(selectToken);
  const location = useLocation()?.pathname;
  const { isLoading } = usePermissions();
  const { getUserAccessibleRedirectLocation } = useRouteConfig();
  const context = useOutletContext();

  // To navigate user to the page they were on before they logged in
  const nextLocation = !token ? location : getUserAccessibleRedirectLocation();

  useCountryVerify(); // doing it here to avoid re-fetching the user country on each render

  if (isLoading) return null;

  return (
    <ProtectedResource
      permission={authorizedPermission}
      fallback={<Navigate to="/login" replace state={{ from: nextLocation }} />}
    >
      <Outlet context={context} />
    </ProtectedResource>
  );
};

export default ProtectedRoute;
